import { formatDateTimeRange } from '@modules/common/utils';
import { InfoOutlined } from '@mui/icons-material';
import DateRangeRoundedIcon from '@mui/icons-material/DateRangeRounded';
import {
  Box,
  Button,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import dayjs, { Dayjs } from 'dayjs';
import { isEqual } from 'lodash';
import {
  forwardRef,
  MouseEvent,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useDeepCompareEffect } from 'react-use';
import { Calendar } from '../Calendar';
import { DEFAULT_DATE_TIME_RANGE } from '../constants';
import { getDateRange, trackDatePickerEvent } from '../helpers';
import { CalendarPickerProps } from '../types';
import { getCalendarLabelByMode, getCalendarTitleByMode } from './helpers';

export interface CalendarPickerActionsHandle {
  openCalendarPicker: (event: MouseEvent) => void;
}

interface LocalCalendarPickerProps extends CalendarPickerProps {
  isDisabled?: boolean;
  renderPickerInput?: (label: string) => JSX.Element;
  shouldDisableTriggerClick?: boolean;
}

export const CalendarPicker = forwardRef<
  CalendarPickerActionsHandle,
  LocalCalendarPickerProps
>(
  (
    {
      dateRange,
      timeRange,
      onChange,
      isDisabled = false,
      renderPickerInput,
      shouldHideDisplayModes,
      shouldDisableTriggerClick,
    },
    forwardedRef,
  ) => {
    const [localTimeRange, setLocalTimeRange] = useState(
      timeRange || DEFAULT_DATE_TIME_RANGE,
    );
    const calendarPickerButton = useRef(null);
    const [shouldOpenMenu, setShouldOpenMenu] = useState(false);

    useDeepCompareEffect(() => {
      setLocalTimeRange(timeRange || DEFAULT_DATE_TIME_RANGE);
    }, [timeRange]);

    const openMenu = (event: MouseEvent) => {
      event.stopPropagation();
      setShouldOpenMenu(true);
      if (timeRange && !isEqual(localTimeRange, timeRange)) {
        setLocalTimeRange(timeRange);
      }
      trackDatePickerEvent({
        eventName: 'Datepicker - Opened',
        dateRange: timeRange ? formatDateTimeRange(timeRange) : null,
      });
    };

    useImperativeHandle(forwardedRef, () => ({
      openCalendarPicker: openMenu,
    }));

    const closeMenu = () => {
      setShouldOpenMenu(false);
      trackDatePickerEvent({
        eventName: 'Datepicker - Closed',
        dateRange: timeRange ? formatDateTimeRange(timeRange) : null,
      });
    };

    const onSubmit = () => {
      localTimeRange && onChange(localTimeRange);
      closeMenu();
      trackDatePickerEvent({
        eventName: 'Datepicker - Applied',
        dateRange: formatDateTimeRange(localTimeRange),
      });
    };

    const datePickerMode = timeRange?.type || 'absolute';

    const localDateRange: DateRange<Dayjs | null> = localTimeRange
      ? getDateRange(localTimeRange)
      : [null, null];
    const shouldAllowSubmit =
      dayjs(localDateRange[0]).isValid() && dayjs(localDateRange[1]).isValid();

    // Use submitted/applied value for button label and title
    const label = getCalendarLabelByMode(dateRange, timeRange, 'Custom');
    const title = getCalendarTitleByMode(dateRange, datePickerMode);

    return (
      <>
        <Box
          onClick={shouldDisableTriggerClick ? undefined : openMenu}
          ref={calendarPickerButton}
          sx={{
            pointerEvents: isDisabled ? 'none' : 'inherit',
            opacity: isDisabled ? 0.5 : 1,
          }}
          aria-label="open-calendar-picker-button"
        >
          {renderPickerInput ? (
            renderPickerInput(label)
          ) : (
            <Tooltip title={title} arrow placement="top">
              <Stack direction="row" spacing={1} alignItems="center">
                <DateRangeRoundedIcon fontSize="small" />
                <Box component="span">{label}</Box>
              </Stack>
            </Tooltip>
          )}
        </Box>
        <Popover
          role="calendar"
          anchorEl={calendarPickerButton.current}
          open={shouldOpenMenu}
          onClose={closeMenu}
          onClick={(eve) => {
            eve.stopPropagation();
            eve.preventDefault();
          }}
        >
          <Calendar
            dateRange={localDateRange}
            timeRange={localTimeRange}
            onChange={setLocalTimeRange}
            shouldHideDisplayModes={shouldHideDisplayModes}
          />
          <Stack
            sx={{ py: 0.5, px: 1.5 }}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <InfoOutlined
                sx={{
                  color: 'text.secondary',
                  fontSize: (theme) => theme.typography.body1.fontSize,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Time Range in UTC
              </Typography>
            </Stack>
            <Stack
              spacing={1}
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
            >
              <Button variant="text" onClick={closeMenu}>
                Cancel
              </Button>
              <Button
                variant="text"
                onClick={onSubmit}
                disabled={!shouldAllowSubmit}
              >
                Apply
              </Button>
            </Stack>
          </Stack>
        </Popover>
      </>
    );
  },
);
